// store/modules/connexionStore.js
import Connexion from '@/script/Connexion.js';
import RequestManager from '@/script/request.js';
import config from '@/config.js';
import { changeTitlePage } from '@/script/index.js';
import Patient from '@/script/Patient.js';
import Values from '@/script/Values.js';
import Client from '@/script/Client.js';

export default {
    namespaced: true,
    state: () => ({
        connecter: localStorage.connecter === "true",
        token: localStorage.getItem('Token') || null,
        appliLance: localStorage.appliLance || null,
        appliLaunched: localStorage.appliLaunched === "true",
        lesApplis: ["Fisimed", "Altyse", "Flic32", "TransCAM"],
        backEndLink: null,
        // Recherche patient (Fisimed/Altyse) : les composants utilisent CE namespace
        // (ConnexionStore/...), pas le module fisimed. La logique vit donc ici.
        patientCourant: {},
        lesPatients: {},
        // Praticien connecte, lu en BASE (Client/getClientWithEmail via l'email du token).
        // Sert a afficher nom/prenom du PS quand il N'Y A PAS de carte CPS (login id/mdp).
        praticien: { nom: '', prenom: '' },
        // Identifiants renvoyes par le natif (SecureStorage) pour preremplir le login en WebView mobile
        identifiantsMobile: { id: '', mdp: '' },
        test: { nom: 'Alias', prenom: 'ALI' }
    }),
    getters: {
        getConnexionValeur: state => state.connecter,
        getToken: state => state.token,
        getAppliLance: state => state.appliLance,
        getAppliLaunched: state => state.appliLaunched,
        getLesApplis: state => state.lesApplis,
        getBackEndLink: state => state.backEndLink,
        getLesPatients: state => state.lesPatients,
        getPraticien: state => state.praticien,
        getPatientCourant: state => {
            const pc = { ...state.patientCourant };
            if (pc.DateNaissance) pc.DateNaissance = new Date(pc.DateNaissance).toLocaleDateString('fr');
            if (pc.DateCreation) pc.DateCreation = new Date(pc.DateCreation).toLocaleDateString('fr');
            return pc;
        },
        getIdentifiantsMobile: state => state.identifiantsMobile,
        getTest: state => state.test
    },
    mutations: {
        setConnexion(state, bool) {
            state.connecter = bool;
            localStorage.connecter = bool;
        },
        setToken(state, token) {
            state.token = token;
            localStorage.setItem('Token', token);
            RequestManager.setToken(token);
        },
        setLinkBackEnd(state, link) {
            state.backEndLink = link;
        },
        setParientCourant(state, patient) {
            state.patientCourant = patient;
        },
        setLesPatientsBySearh(state, patients) {
            state.lesPatients = patients;
        },
        setPraticien(state, p) {
            state.praticien = { nom: (p && p.nom) || '', prenom: (p && p.prenom) || '' };
        },
        setIdentifiantsMobile(state, creds) {
            state.identifiantsMobile = { id: (creds && creds.id) || '', mdp: (creds && creds.mdp) || '' };
        },
        setAppliLance(state, appli) {
            state.appliLance = appli;
            localStorage.appliLance = appli;
            changeTitlePage(appli);
        },
        setAppliLaunched(state, bool) {
            state.appliLaunched = bool;
            localStorage.appliLaunched = bool;
        },
        deconnexion(state) {
            state.connecter = false;
            state.token = null;
            state.appliLance = null;
            state.appliLaunched = false;
            state.backEndLink = null;
            state.praticien = { nom: '', prenom: '' };
            localStorage.clear();
            RequestManager.setToken("");
        }
    },
    actions: {
        // Normalise le BackEndLink renvoye par le backend : c'est desormais une URL
        // COMPLETE (serveurs.lien) ou vide/null si le compte n'a pas d'instance backend
        // en ligne (contrat.idServeur NULL) -> mode local, "pas de backend disponible".
        appliquerConnexion({ commit }, obj) {
            const connected = obj && obj.success === true;
            commit("setConnexion", connected);
            if (obj && obj.Token) commit("setToken", obj.Token);

            let lien = (obj && obj.BackEndLink && obj.BackEndLink.trim() !== "")
                ? obj.BackEndLink.trim()
                : null;

            // Si le backend est servi par le serveur de login (meme machine), le faire suivre l'ORIGINE
            // REELLE de la page (gere la bascule sur l'IP de secours sous WireGuard, cf config.js).
            // IMPORTANT : on garde une URL ABSOLUE (origine + path), pas relative : le code prefixe a la
            // fois config.urlBackEnd ET axios baseURL avec cette valeur -> une URL absolue evite le
            // double-prefixe (sinon on obtient /final/final/...). Les comptes clients sur un AUTRE
            // serveur restent inchanges.
            if (lien) {
                try {
                    const u = new URL(lien, window.location.origin);
                    if (u.hostname === "login.fisi.fr") {
                        lien = window.location.origin + u.pathname + u.search; // ex: https://192.168.2.201/final
                    }
                } catch (e) { /* lien deja relatif : on garde tel quel */ }
            }
            commit("setLinkBackEnd", lien);
            config.urlSubBackEnd = lien;

            if (lien) {
                localStorage.urlSubBackEnd = lien;
                // Les scripts data (Patient, Acte, FSE...) utilisent config.urlBackEnd comme
                // base. Apres login il doit pointer vers le backend du CLIENT (BackEndLink),
                // pas vers le parent (qui n'a que le login). Slash final garanti car les scripts
                // font tantot "urlBackEnd + X" tantot "urlBackEnd + /X" (le double slash est tolere).
                const baseData = lien.endsWith("/") ? lien : lien + "/";
                config.urlBackEnd = baseData;
                RequestManager.instance.defaults.baseURL = baseData;
            } else {
                localStorage.removeItem("urlSubBackEnd");
            }

            return { success: connected, hasBackend: !!lien, resMsg: obj ? obj.resMsg : null };
        },
        async connecter({ commit, dispatch }, { id, mdp }) {
            const response = await Connexion.connecter(id, mdp);
            return dispatch("appliquerConnexion", response);
        },
        setConnexion({ dispatch }, obj) {
            if (obj) dispatch("appliquerConnexion", obj);
        },
        deconnexion({ commit }) {
            // Efface aussi les identifiants stockes cote natif (SecureStorage) en WebView mobile
            RequestManager.EnvoyerMessageWebView({ action: "effacerIdentifiants", parameters: [] });
            commit("deconnexion");
        },
        setAppliLance({ commit, getters }, appli) {
            commit("setAppliLance", appli.name);
            const isValid = getters.getLesApplis.includes(appli.name);
            commit("setAppliLaunched", isValid);
        },
        setAppliLaunched({ commit }, bool) {
            commit("setAppliLaunched", bool);
        },
        // Recherche patient (Fisimed/Altyse) — portee depuis le module fisimed (mort) car les
        // composants dispatchent ConnexionStore/setPatientCourant et /setLesPatientsBySearh.
        async setPatientCourant({ commit, state }, id) {
            if (id) {
                const appliLance = (state.appliLance || "fisimed").toLowerCase();
                const response = await Patient.getPatientById(id, appliLance);
                commit('setParientCourant', response);
            }
        },
        async setLesPatientsBySearh({ commit, state }, critere) {
            if (!critere) critere = "a";
            // Fisimed/Altyse : endpoint "app-aware" qui selectionne la bonne base via appliLance
            // (fisimed, altyse...) et renvoie deja un tableau [{Key, Value:{Nom,...}}] en PascalCase,
            // directement compatible Grid.vue. (getPatientBySearch sans app tapait la base TransCAM
            // -> ADRUN/ADRDEUX/ADRTROIS de test.)
            const appliLance = (state.appliLance || "fisimed").toLowerCase();
            const response = await Patient.getPatientsBySearchWithApp(critere, appliLance);
            // Tolerant : tableau direct (cas normal) ou ancienne forme { Patients: { dict } } en secours.
            let lesPatients = [];
            if (Array.isArray(response)) {
                lesPatients = response;
            } else {
                const dict = response ? (response.Patients || response.patients) : null;
                if (dict) {
                    lesPatients = Object.entries(dict).map(([key, p]) => ({
                        Key: key,
                        Value: {
                            Nom: p.Nom ?? p.nom,
                            Prenom: p.Prenom ?? p.prenom,
                            CodePostal: p.CodePostal ?? p.codePostal,
                            ...p
                        }
                    }));
                }
            }
            commit("setLesPatientsBySearh", lesPatients);
        },
        // Charge le praticien connecte depuis la BASE : claims (email du token) -> Client.
        // Utilise pour afficher nom/prenom du PS sans carte CPS (connexion identifiant/mdp).
        async chargerPraticien({ commit, state }) {
            try {
                // Garde : si le praticien est deja charge, on ne refait PAS l'appel backend.
                // Sinon Client/getClientWithEmail (-> chargerSituationSansCPS) serait rejoue a
                // chaque (re)montage de TransCAM (navigation + watch connexionValeur d'App.vue).
                if (state.praticien && (state.praticien.nom || state.praticien.prenom)) return;
                const claims = await Values.GetClaims(state.token);
                const email = claims && claims.email;
                if (!email) return;
                const client = await Client.getClientWithEmail(email);
                if (client && (client.nom || client.prenom)) {
                    commit('setPraticien', { nom: client.nom, prenom: client.prenom });
                }
            } catch (e) {
                console.error('ConnexionStore.chargerPraticien -->', e);
            }
        }
    }
};
